import { alpha, Theme, SxProps, darken } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import * as hooks from "lib/hooks";

type buttonVariant = "contained" | "outlined" | "text" | undefined;
type buttonColor =
	| "primary"
	| "secondary"
	| "inherit"
	| "error"
	| "info"
	| "success"
	| "warning"
	| undefined;

export const GetMaterialYouButtonStyles = (
	theme: Theme,
	variant: buttonVariant,
	color: buttonColor
): SxProps<Theme> => {
	const materialColors = hooks.UseMaterialColors();

	const isPrimary = color === "primary" || !color;
	const isSecondary = color === "secondary";
	const isOutlined = variant === "outlined";
	const isContained = variant === "contained" || !variant;
	const isText = variant === "text";

	const getColor = (colorName: string) => {
		if (isPrimary) return materialColors.primary;
		if (isSecondary) return materialColors.secondary;
		return theme.palette[colorName]?.main || materialColors.primary;
	};

	const primaryColor = getColor("primary");

	const baseStyles = {
		borderRadius: materialColors.shape.corner.medium,
		textTransform: "none",
		fontWeight: 500,
		height: 44,
		padding: "0 20px",
		letterSpacing: "0.01em",
		fontSize: "0.875rem",
		fontFamily: "'Google Sans', 'Roboto', sans-serif",
		whiteSpace: "nowrap",
		transition: "all 0.25s cubic-bezier(0.2, 0.0, 0.0, 1.0)",
		position: "relative",
		overflow: "hidden",
	};

	const emphasizedStateTransitions = {
		"&:hover": { transform: "translateY(-1px)" },
		"&:active": {
			transform: "translateY(1px)",
			transition: "all 0.1s cubic-bezier(0.4, 0.0, 1.0, 1.0)",
		},
	};

	const darkModeStyles = isOutlined
		? {
			...(primaryColor && { borderColor: alpha(primaryColor, 0.7) }),
			color: primaryColor,
			"&:hover": {
				backgroundColor: alpha(primaryColor, 0.2),
			},
			"&:active": {
				backgroundColor: alpha(primaryColor, 0.12),
				transform: "translateY(1px)",
			},
		}
		: {};

	const lightModeStyles = isContained
		? {
			background: `linear-gradient(45deg, ${primaryColor}, ${alpha(
				primaryColor,
				0.9
			)})`,
			boxShadow: `0 2px 8px ${alpha(primaryColor, 0.2)}`,
			"&:hover": {
				background: `linear-gradient(45deg, ${primaryColor}, ${alpha(
					primaryColor,
					0.8
				)})`,
			},
			"&:active": {
				boxShadow: `0 1px 4px ${alpha(primaryColor, 0.15)}`,
				transform: "translateY(1px)",
			},
		}
		: {};

	const disabledStyles = {
		"&.Mui-disabled": {
			backgroundColor: isContained
				? materialColors.isDark
					? "rgba(30, 30, 30, 0.7)"
					: "#E0E0E0"
				: "transparent",
			color: materialColors.isDark ? "#FFFFFF" : "#757575",
			opacity: 1,
			boxShadow: "none",
			borderColor:
				isOutlined && materialColors.isDark
					? "rgba(255, 255, 255, 0.3)"
					: isOutlined
						? "rgba(0, 0, 0, 0.26)"
						: "transparent",
			"&::after": isContained
				? {
					content: '""',
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					borderRadius: "inherit",
					boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.12)",
					pointerEvents: "none",
				}
				: {},
			filter: "none",
			fontWeight: 500,
			backgroundImage: "none",
			cursor: "not-allowed",
			"&:hover, &:active": {
				transform: "none",
				boxShadow: "none",
				backgroundColor: isContained
					? materialColors.isDark
						? "rgba(30, 30, 30, 0.7)"
						: "#E0E0E0"
					: "transparent",
			},
		},
	};

	const textButtonStyles = isText
		? {
			padding: "8px 16px",
			height: "auto",
			color: primaryColor,
			"&:hover": {
				backgroundColor: alpha(primaryColor, 0.05),
			},
			"&:active": {
				backgroundColor: alpha(primaryColor, 0.1),
			},
			"&.Mui-disabled": {
				backgroundColor: "transparent",
				color: materialColors.isDark
					? "rgba(255, 255, 255, 0.5)"
					: "rgba(0, 0, 0, 0.38)",
				opacity: 1,
				textDecoration: "none",
				cursor: "not-allowed",
			},
		}
		: {};

	return {
		...baseStyles,
		...emphasizedStateTransitions,
		...(materialColors.isDark ? darkModeStyles : lightModeStyles),
		...disabledStyles,
		...textButtonStyles,
	};
};


export const LoadingButtonStyle = (theme: Theme): SystemStyleObject<Theme> => {
	const isDark = theme.palette.mode === "dark";

	const primary = theme.palette.primary.main;
	const primaryLight = theme.palette.primary.light;
	const primaryDark = theme.palette.primary.dark;
	const contrastText = theme.palette.getContrastText(primary);

	return {
		borderRadius: "8px !important",
		textTransform: "none",
		py: 1.25,
		px: 4,
		fontWeight: 600,
		fontSize: "0.95rem",
		letterSpacing: "0.015em",
		color: contrastText,
		background: isDark
			? darken(primary, 0.2)
			: `linear-gradient(135deg, ${primary}, ${primaryDark})`,
		boxShadow: isDark
			? `0 2px 4px ${alpha("#000", 0.6)}`
			: `0 3px 6px ${alpha(primary, 0.25)}`,
		transition: "all 0.2s ease",

		"&:hover": {
			background: isDark
				? darken(primary, 0.1)
				: `linear-gradient(135deg, ${primaryDark}, ${primary})`,
			boxShadow: isDark
				? `0 3px 6px ${alpha("#000", 0.5)}`
				: `0 4px 10px ${alpha(primary, 0.35)}`,
		},

		"&:active": {
			transform: "translateY(1px)",
			boxShadow: "none",
		},

		"&.Mui-disabled": {
			color: theme.palette.action.disabled,
			background: theme.palette.action.disabledBackground,
			boxShadow: "none",
			cursor: "not-allowed",
			pointerEvents: "none",
		},
	};
};

export const MotionPrimaryButtonStyle = (theme: Theme) => ({
	py: 1.5,
	px: 4,
	borderRadius: 3,
	textTransform: "none",
	fontWeight: 600,
	boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
	background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
	"&:hover": {
		background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
		boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
	},
});

export const MotionOutlinedButtonStyle = {
	py: 1.5,
	px: 4,
	borderRadius: 3,
	textTransform: "none",
	fontWeight: 600,
};

export const StepButtonWrapper = {
	display: "flex",
	justifyContent: "flex-end",
	mt: 2,
	pt: 2,
	position: "relative",
};